import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { listAllPettyCash, setPettyFloat } from "@/lib/api";
import { Empty, TH, TR, errMsg, money } from "@/components/accounts/shared";

// Petty cash across gyms: the fixed float, expected cash, the last weekly count and its difference.
export default function PettyCashAll({ country, gym, gyms }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => listAllPettyCash().then(setRows).catch((e) => toast.error(errMsg(e, "Could not load petty cash"))), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const ctry = Object.fromEntries(gyms.map((g) => [g.id, g.country]));
  const visible = rows.filter((r) => (!gym || r.facility_id === gym) && (!country || ctry[r.facility_id] === country));
  const saveFloat = async (r, v) => {
    if (Number(v) === r.float_amount) return;
    try { await setPettyFloat(r.facility_id, Number(v) || 0); toast.success("Float saved"); load(); } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  return (
    <div className="card-surface overflow-x-auto" data-testid="accounts-petty">
      <div className="px-4 py-3 text-[11px] text-zinc-500">Each gym keeps a fixed float, tops it up when low and counts it weekly (FPA → Petty cash). Spends go into Expenses with their receipt.</div>
      {visible.length === 0 ? <Empty>No gyms.</Empty> : (
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className={TH}><th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">Float</th>
            <th className="px-3 py-2 font-medium text-right">Expected cash</th><th className="px-3 py-2 font-medium">Last count</th>
            <th className="px-3 py-2 font-medium text-right">Difference</th><th className="px-3 py-2 font-medium">Recent</th></tr></thead>
          <tbody>
            {visible.map((r) => {
              const c = r.counts[0];
              return (
                <tr key={r.facility_id} className={`${TR} align-top`}>
                  <td className="px-3 py-2 text-zinc-200">{r.gym}</td>
                  <td className="px-3 py-2"><Input type="number" defaultValue={r.float_amount || ""} onBlur={(e) => saveFloat(r, e.target.value)} placeholder="Set float" className="h-8 w-28 bg-[#0a0a0a] border-[#262626]" /></td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.low ? "text-amber-300" : ""}`}>{money(r, r.expected)}{r.low && <div className="text-[10px]">low: top up</div>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{c ? `${c.date} · ${c.by}` : "never"}{r.count_due && <div className="text-[10px] text-amber-300">weekly count due</div>}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${c && Math.abs(c.difference) >= 1 ? "text-rose-300" : "text-zinc-400"}`}>{c ? money(r, c.difference) : "—"}{c?.note && <div className="text-[10px] text-zinc-500">{c.note}</div>}</td>
                  <td className="px-3 py-2 text-[11px] text-zinc-500">{r.txns.slice(0, 3).map((t) => <div key={t.id}>{t.date} {t.kind === "spend" ? "−" : "+"}{money(r, t.amount)} {t.category && t.kind === "spend" ? t.category : "top-up"}</div>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
